import React, { useState, useRef, useEffect, Fragment, useCallback, useMemo } from 'react';
import { MessageCircle, Briefcase, Heart, Lightbulb, Plus, ArrowLeft, ChevronLeft, ChevronRight, Send } from 'lucide-react';
import { Dialog, Transition } from '@headlessui/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ChatHistory from './ChatHistory';
import { formatDistanceToNow } from 'date-fns';
import * as WefayoAI from '../../core/wefayo_ai';
import NavLogo from '../navigation/NavLogo';
import { general_instructions } from './PreContext';

// Isolated textarea component to prevent focus loss
const IsolatedTextarea = React.memo(React.forwardRef(({ onSend, disabled, conversationId }, ref) => {
    const textareaRef = useRef(null);
    const valueRef = useRef('');

    const handleChange = useCallback((e) => {
        valueRef.current = e.target.value;
    }, []);

    const sendMessage = useCallback(() => {
        if (valueRef.current.trim() && !disabled) {
            onSend(valueRef.current.trim());
            if (textareaRef.current) {
                textareaRef.current.value = '';
                valueRef.current = '';
            }
        }
    }, [onSend, disabled]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    }, [sendMessage]);

    // Expose send function to parent via ref
    React.useImperativeHandle(ref, () => ({
        sendMessage
    }), [sendMessage]);

    // Clear on conversation change
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.value = '';
            valueRef.current = '';
        }
    }, [conversationId]);

    return (
        <textarea
            ref={textareaRef}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            rows={2}
            className="flex-1 rounded-md border border-gray-300 px-4 py-2 md:py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
            disabled={disabled}
        />
    );
}));

const agents = [
    {
        id: 'conversationist',
        name: 'Conversation Coach',
        icon: MessageCircle,
        description: 'Engage in meaningful conversations and discussions',
        systemPrompt: general_instructions('conversationist', 'AI assistant who specializes in engaging, meaningful dialogue. You ask thoughtful questions and provide insightful responses to keep conversations flowing naturally.'),
        details: "Practice writing, talking, asking questions, and role playing conversations before doing them of just chat about anything on your mind",
        greeting: "Hi, I'm your Conversationist. I'm here to chat, help you practice talking, writing, asking questions, or role-play conversations to prepare before you have them. Or we can just talk about anything on your mind."
    },
    {
        id: 'companionist',
        name: 'Companion',
        icon: Heart,
        description: 'Your friendly companion for emotional support',
        systemPrompt: general_instructions('companionist', 'You are a supportive and empathetic companion. You provide emotional support, active listening, and encouragement. You are warm, understanding, and always ready to offer comfort and friendship.'),
        details: "Talk about your day, feelings, anxiety, sensory overload, or anything else weighing on you; a safe space to share, vent, and find calming support",
        greeting: "Hi, I'm your Companion. I'm here for emotional support. You can tell me about your day, your feelings, anxiety, stress, sensory overload, whatever's weighing on you. This is a safe space to share, vent, and feel supported."
    },
    {
        id: 'careerist',
        name: 'Career Support',
        icon: Briefcase,
        description: 'Explore job and career topics',
        systemPrompt: general_instructions('careerist', 'You are a career counselor and professional development expert. You provide guidance on career paths, job searches, interview preparation, skill development, and workplace challenges.'),
        details: "Get Help with your resume, interview preparations, or tricky work situations",
        greeting: "Hi, I'm your Career Helper. I can help you apply for jobs, write your resume, prepare for interviews, or talk through tough or confusing work situations. I'm here to make things a little easier."
    },
    {
        id: 'clarifyist',
        name: 'Organizer',
        icon: Lightbulb,
        description: 'Simplify your tasks your tasks and routines',
        systemPrompt: general_instructions('clarifyist', 'You are a task management and organization expert. You help break down complex tasks into manageable steps, create clear action plans, organize daily routines, and make complicated things easier to understand and accomplish.'),
        details: "Get help breaking down tasks, understanding steps, managing daily routines, and making things easier to do",
        greeting: "Hi, I'm your Organizer. I can help you with hard tasks by breaking them into small steps. I'll also provide tips on remembering things and make simple routines for daily life. Let's take it one piece at a time."
    }
];

const WefayoAIChatInterface = ({ isModal = false, isOpen = true, closeFun = null, selectedAgentId = null }) => {
    const [showAgentSelection, setShowAgentSelection] = useState(true);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [currentSession, setCurrentSession] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [currentConversationId, setCurrentConversationId] = useState(null);
    const [showChatHistory, setShowChatHistory] = useState(true);
    const [userSessions, setUserSessions] = useState([]); // All user sessions
    const [isLoadingInitial, setIsLoadingInitial] = useState(false);
    const [isLoadingAgent, setIsLoadingAgent] = useState(false);
    const messagesContainerRef = useRef(null);
    const textareaRef = useRef(null);
    const isolatedTextareaRef = useRef(null);
    const inputValueRef = useRef('');
    const [messageBeingSent, setMessageBeingSent] = useState('');// Store input value in ref to avoid re-renders

    const handleSendMessage = useCallback(async (messageToSend) => {
        if (!messageToSend || isLoading || !currentSession || !currentConversationId) {
            return;
        }

        setMessageBeingSent(messageToSend);

        // Store current scroll position before any state changes
        // const currentScrollTop = messagesContainerRef.current?.scrollTop; cu:
        // const currentScrollHeight = messagesContainerRef.current?.scrollHeight; cu:

        // Immediately scroll to bottom when user sends message
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }

        setIsLoading(true);

        // Restore scroll position after state change to prevent jump
        requestAnimationFrame(() => {
            if (messagesContainerRef.current) {
                messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            }
        });

        try {
            const data = await WefayoAI.sendChatMessage({
                sessionId: currentSession._id,
                message: messageToSend,
                conversationId: currentConversationId
            });

            if (data instanceof Error) {
                throw new Error(data.message || 'Failed to send message');
            }

            // console.log('🔍 Response from sendChatMessage:', data);
            // console.log('🔍 data.session:', data.session);
            // console.log('🔍 data.session.conversations:', data.session?.conversations);

            // Update states with the response
            if (data.session) {
                const updatedConversation = data.session.conversations.find(
                    conv => conv._id === data.session.currentConversationId
                );

                // console.log('🔍 Found updated conversation:', updatedConversation);
                // console.log('🔍 Updated conversation title:', updatedConversation?.title);

                if (updatedConversation) {
                    const updatedMessages = [...updatedConversation.messages];
                    setMessages(updatedMessages);

                    // Immediately scroll to bottom after updating messages
                    setTimeout(() => {
                        if (messagesContainerRef.current) {
                            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
                        }
                    }, 0);

                    // Update conversations while preserving conversations from other sessions
                    setConversations(prevConversations => {
                        // Remove old conversations from this session and add updated ones
                        const otherSessionConversations = prevConversations.filter(
                            conv => conv.sessionId !== currentSession._id
                        );

                        // Add updated conversations from current session with session info
                        const updatedSessionConversations = data.session.conversations.map(conv => ({
                            ...conv,
                            sessionId: data.session._id,
                            sessionUpdatedAt: data.session.updatedAt
                        }));

                        // console.log('🔍 Updated session conversations:', updatedSessionConversations);
                        // console.log('🔍 Conversation titles in updated data:', updatedSessionConversations.map(c => ({ id: c._id, title: c.title })));

                        // Combine and sort by most recent activity
                        const allConversations = [...otherSessionConversations, ...updatedSessionConversations];
                        return allConversations.sort((a, b) =>
                            new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
                        );
                    });

                    setCurrentSession(data.session);

                    if (data.session.currentConversationId !== currentConversationId) {
                        setCurrentConversationId(data.session.currentConversationId);
                    }
                }
            }
        } catch (error) {
            console.error('Error sending message:', error);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: `Error: ${error.message}`,
                timestamp: new Date(),
                isError: true
            }]);
        } finally {
            setIsLoading(false);
            // Ensure we're at bottom after loading completes
            setTimeout(() => {
                if (messagesContainerRef.current) {
                    messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
                }
            }, 0);
        }
    }, [isLoading, currentSession, currentConversationId]);

    const handleSendButtonClick = useCallback(() => {
        if (isolatedTextareaRef.current) {
            isolatedTextareaRef.current.sendMessage();
        }
    }, []);

    // Memoized disabled states to prevent unnecessary re-renders
    const isTextareaDisabled = useMemo(() => {
        return isLoading || !currentConversationId;
    }, [isLoading, currentConversationId]);

    const isSendButtonDisabled = useMemo(() => {
        return isLoading || !currentConversationId;
    }, [isLoading, currentConversationId]);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        if (messagesContainerRef.current) {
            // Add a small delay to avoid conflicts with manual scroll calls
            const timeoutId = setTimeout(() => {
                if (messagesContainerRef.current) {
                    messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
                }
            }, 50); // Small delay to let other scroll operations complete

            return () => clearTimeout(timeoutId);
        }
    }, [messages]); // Depend on entire messages array, not just length

    // Fetch user's existing sessions when component mounts
    useEffect(() => {
        if (isOpen && !isLoadingInitial) {
            fetchUserSessions();
        }
    }, [isOpen]);

    // Clear textarea when conversation changes
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.value = '';
            inputValueRef.current = '';
            setInputMessage('');
        }
    }, [currentConversationId, showAgentSelection]);

    const fetchUserSessions = async () => {
        try {
            setIsLoadingInitial(true);
            const data = await WefayoAI.getChatHistory();

            if (data && !(data instanceof Error) && data.sessions) {
                setUserSessions(data.sessions);
            }
        } catch (error) {
            console.error('Error fetching user sessions:', error);
        } finally {
            setIsLoadingInitial(false);
        }
    };

    const handleAgentSelect = async (agent) => {
        try {
            setSelectedAgent(agent);
            setIsLoading(true);
            setIsLoadingAgent(true);

            // Get all active sessions for this agent
            const agentSessions = userSessions.filter(
                session => session.agentType === agent.id && session.isActive
            );

            let allConversations = [];
            let currentSessionData = null;
            let currentConversationId = null;

            if (agentSessions.length > 0) {
                // Make all API calls in parallel instead of sequential
                const sessionPromises = agentSessions.map(async (session) => {
                    try {
                        // Add timeout to prevent hanging requests
                        const timeoutPromise = new Promise((_, reject) =>
                            setTimeout(() => reject(new Error('Request timeout')), 10000)
                        );

                        const sessionPromise = WefayoAI.getSession(session._id);
                        const freshData = await Promise.race([sessionPromise, timeoutPromise]);

                        if (freshData && !(freshData instanceof Error)) {
                            const sessionData = freshData.session || freshData;
                            return {
                                success: true,
                                sessionData,
                                originalSession: session
                            };
                        }
                        return {
                            success: false,
                            sessionData: session,
                            originalSession: session,
                            error: 'Failed to fetch session data'
                        };
                    } catch (error) {
                        return {
                            success: false,
                            sessionData: session,
                            originalSession: session,
                            error: error.message
                        };
                    }
                });

                // Wait for all API calls to complete with a maximum timeout
                const allSessionsPromise = Promise.all(sessionPromises);
                const timeoutPromise = new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('All sessions timeout')), 15000)
                );

                const sessionResults = await Promise.race([allSessionsPromise, timeoutPromise]);

                // Process all results
                sessionResults.forEach(result => {
                    // Add session info to each conversation for tracking
                    const sessionConversations = (result.sessionData.conversations || []).map(conv => ({
                        ...conv,
                        sessionId: result.sessionData._id,
                        sessionUpdatedAt: result.sessionData.updatedAt
                    }));

                    allConversations = [...allConversations, ...sessionConversations];

                    // Use the most recently updated session as the current session
                    if (!currentSessionData ||
                        new Date(result.sessionData.updatedAt) > new Date(currentSessionData.updatedAt)) {
                        currentSessionData = result.sessionData;
                        currentConversationId = result.sessionData.currentConversationId;
                    }
                });

                // Sort conversations by most recent activity
                allConversations.sort((a, b) =>
                    new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
                );

            } else {
                // Create new session if no existing sessions
                const data = await WefayoAI.startChatSession(agent.id);
                if (data instanceof Error) {
                    throw new Error('Failed to start chat session');
                }

                currentSessionData = data.session;
                // Add session info to each conversation for tracking
                allConversations = (data.session.conversations || []).map(conv => ({
                    ...conv,
                    sessionId: data.session._id,
                    sessionUpdatedAt: data.session.updatedAt
                }));
                currentConversationId = data.session.currentConversationId;

                // Refresh user sessions to include the new one
                await fetchUserSessions();
            }

            setCurrentSession(currentSessionData);
            setConversations(allConversations);
            setCurrentConversationId(currentConversationId);

            // Find and set the current conversation messages
            if (currentConversationId) {
                const currentConversation = allConversations.find(
                    conv => conv._id === currentConversationId
                );
                if (currentConversation) {
                    setMessages(currentConversation.messages || []);
                }
            } else if (allConversations.length > 0) {
                // If no current conversation ID but conversations exist, use the most recent one
                const mostRecentConversation = allConversations[0]; // Already sorted by recent activity
                setCurrentConversationId(mostRecentConversation._id);
                setMessages(mostRecentConversation.messages || []);
            }

            setShowAgentSelection(false);
        } catch (error) {
            console.error('Error starting chat session:', error);
        } finally {
            setIsLoading(false);
            setIsLoadingAgent(false);
        }
    };

    useEffect(() => {
        if (selectedAgentId) {
            // find the agent by id
            const agent = agents.find(agent => agent.id === selectedAgentId);
            if (agent) {
                handleAgentSelect(agent);
            }
        }
    }, [selectedAgentId]);

    const handleNewConversation = async () => {
        if (!selectedAgent) {
            console.error('No agent selected for creating new conversation');
            return;
        }

        try {
            setIsLoading(true);
            let data;

            // If we have an active session, try to create conversation in it
            if (currentSession && currentSession._id) {
                data = await WefayoAI.createNewConversation(currentSession._id);
            }

            // If no session or creating conversation fails, create a new session
            if (!currentSession || !currentSession._id || data instanceof Error) {
                if (data instanceof Error) {
                    // console.log('Current session inactive, creating new session...');
                } else {
                    // console.log('No active session, creating new session...');
                }
                
                const newSessionData = await WefayoAI.startChatSession(selectedAgent.id);
                
                if (newSessionData instanceof Error) {
                    throw new Error('Failed to create new session');
                }

                data = newSessionData;
                await fetchUserSessions(); // Refresh user sessions
            }

            if (!data || !data.session) {
                throw new Error('Invalid response from server - no session data');
            }

            // Update conversations while preserving conversations from other sessions
            setConversations(prevConversations => {
                // Remove old conversations from this session and add updated ones
                const otherSessionConversations = prevConversations.filter(
                    conv => conv.sessionId !== data.session._id
                );

                // Add updated conversations from current session with session info
                const updatedSessionConversations = (data.session.conversations || []).map(conv => ({
                    ...conv,
                    sessionId: data.session._id,
                    sessionUpdatedAt: data.session.updatedAt
                }));

                // Combine and sort by most recent activity
                const allConversations = [...otherSessionConversations, ...updatedSessionConversations];
                return allConversations.sort((a, b) =>
                    new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
                );
            });

            setCurrentConversationId(data.session.currentConversationId);
            setCurrentSession(data.session);
            setMessages([]);
        } catch (error) {
            console.error('Error creating new conversation:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelectConversation = async (conversation) => {
        // if (conversation._id === currentConversationId) return;

        // console.log('conversation::', conversation);

        try {
            setShowChatHistory(false);
            setIsLoading(true);

            // Check if this conversation belongs to the current session
            if (conversation.sessionId === currentSession._id) {
                // Same session - just switch conversation
                const data = await WefayoAI.switchConversation(currentSession._id, conversation._id);

                if (data instanceof Error) {
                    throw new Error('Failed to switch conversation');
                }

                setCurrentConversationId(conversation._id);
                setMessages(conversation.messages || []);

                // Update current session data
                if (data.session) {
                    setCurrentSession(data.session);
                    // Update conversations list while preserving cross-session conversations
                    const updatedConversations = conversations.map(conv => {
                        if (conv.sessionId === data.session._id) {
                            const updatedConv = data.session.conversations.find(c => c._id === conv._id);
                            if (updatedConv) {
                                // Preserve frontend-added properties like sessionId
                                return {
                                    ...updatedConv,
                                    sessionId: conv.sessionId,
                                    sessionUpdatedAt: conv.sessionUpdatedAt
                                };
                            }
                        }
                        return conv;
                    });
                    setConversations(updatedConversations);
                }
            } else {
                // Different session - switch to that session first
                const sessionData = await WefayoAI.getSession(conversation.sessionId);
                if (sessionData instanceof Error) {
                    throw new Error('Failed to switch to conversation session');
                }

                const session = sessionData.session || sessionData;

                // Switch to the conversation in that session
                const switchData = await WefayoAI.switchConversation(session._id, conversation._id);

                if (switchData instanceof Error) {
                    throw new Error('Failed to switch conversation');
                }

                setCurrentSession(switchData.session || session);
                setCurrentConversationId(conversation._id);
                setMessages(conversation.messages || []);
            }
        } catch (error) {
            console.error('Error switching conversation:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteConversation = async (conversation) => {
        if (!conversation.sessionId || !conversation._id) {
            console.error('Invalid conversation data for deletion');
            return;
        }

        try {
            setIsLoading(true);
            const data = await WefayoAI.deactivateConversation(conversation.sessionId, conversation._id);

            if (data instanceof Error) {
                throw new Error('Failed to delete conversation');
            }

            // Remove the conversation from the local state
            setConversations(prevConversations =>
                prevConversations.filter(conv => conv._id !== conversation._id)
            );

            // Check if we deleted all conversations from the current session
            const remainingSessionConversations = conversations.filter(
                conv => conv._id !== conversation._id && conv.sessionId === conversation.sessionId
            );

            // If we deleted the current conversation, clear the messages and conversation ID
            if (conversation._id === currentConversationId) {
                setMessages([]);
                setCurrentConversationId(null);

                // If there are other conversations, switch to the most recent one
                const allRemainingConversations = conversations.filter(conv => conv._id !== conversation._id);
                if (allRemainingConversations.length > 0) {
                    const mostRecentConversation = allRemainingConversations.sort((a, b) =>
                        new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
                    )[0];
                    handleSelectConversation(mostRecentConversation);
                } else {
                    // No conversations left at all, clear the current session
                    setCurrentSession(null);
                }
            } else if (remainingSessionConversations.length === 0 && conversation.sessionId === currentSession?._id) {
                // If we deleted all conversations from the current session but it wasn't the active conversation
                setCurrentSession(null);
                setCurrentConversationId(null);
                setMessages([]);
            }
        } catch (error) {
            console.error('Error deleting conversation:', error);
            alert('Failed to delete conversation. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToAgents = () => {
        setShowAgentSelection(true);
        setSelectedAgent(null);
        setMessages([]);
        setInputMessage('');
        setCurrentSession(null);
        setConversations([]);
        setCurrentConversationId(null);
    };

    const MessageBubble = ({ message, index, showTimestamp = true }) => {
        return (
            <div
                key={message._id || index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'
                    }`}
            >
                <div
                    className={`max-w-[80%] rounded-lg p-3 ${message.isError
                        ? 'bg-red-100 text-red-800 border border-red-200'
                        : message.role === 'user'
                            ? 'bg-purple-600 text-white rounded-br-none'
                            : 'bg-gray-100 text-gray-800 rounded-bl-none'
                        }`}
                >
                    <div className={`${message.role === 'user' ? 'text-white' : 'text-gray-800'
                        }`}>
                        <div className="markdown-content">
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    p: ({ node, ...props }) => <p className="mb-3 last:mb-0 leading-relaxed" {...props} />,
                                    ul: ({ node, ...props }) => <ul className="list-disc list-inside mb-3 space-y-1" {...props} />,
                                    ol: ({ node, ...props }) => <ol className="list-decimal list-inside mb-3 space-y-1" {...props} />,
                                    li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
                                    code: ({ node, inline, ...props }) =>
                                        inline
                                            ? <code className={`px-1.5 py-0.5 rounded text-sm font-mono ${message.role === 'user'
                                                ? 'bg-purple-700 bg-opacity-50 text-purple-100'
                                                : 'bg-gray-200 text-gray-800'
                                                }`} {...props} />
                                            : <pre className={`p-3 rounded-md text-sm overflow-x-auto font-mono my-3 ${message.role === 'user'
                                                ? 'bg-purple-700 bg-opacity-50 text-purple-100'
                                                : 'bg-gray-200 text-gray-800'
                                                }`}><code {...props} /></pre>,
                                    blockquote: ({ node, ...props }) => <blockquote className={`border-l-4 pl-4 italic my-3 ${message.role === 'user'
                                        ? 'border-purple-300 border-opacity-50'
                                        : 'border-gray-300'
                                        }`} {...props} />,
                                    h1: ({ node, ...props }) => <h1 className="text-xl font-bold mb-3 mt-4 first:mt-0" {...props} />,
                                    h2: ({ node, ...props }) => <h2 className="text-lg font-bold mb-2 mt-3 first:mt-0" {...props} />,
                                    h3: ({ node, ...props }) => <h3 className="text-base font-bold mb-2 mt-3 first:mt-0" {...props} />,
                                    h4: ({ node, ...props }) => <h4 className="text-sm font-bold mb-1 mt-2 first:mt-0" {...props} />,
                                    strong: ({ node, ...props }) => <strong className="font-bold" {...props} />,
                                    em: ({ node, ...props }) => <em className="italic" {...props} />,
                                    a: ({ node, ...props }) => <a className={`underline ${message.role === 'user'
                                        ? 'text-purple-200 hover:text-purple-100'
                                        : 'text-purple-600 hover:text-purple-800'
                                        }`} target="_blank" rel="noopener noreferrer" {...props} />,
                                    table: ({ node, ...props }) => <div className="overflow-x-auto my-3"><table className={`min-w-full border-collapse ${message.role === 'user'
                                        ? 'border-purple-300 border-opacity-50'
                                        : 'border-gray-300'
                                        }`} {...props} /></div>,
                                    th: ({ node, ...props }) => <th className={`border px-3 py-2 text-left font-bold ${message.role === 'user'
                                        ? 'border-purple-300 border-opacity-50 bg-purple-700 bg-opacity-25'
                                        : 'border-gray-300 bg-gray-100'
                                        }`} {...props} />,
                                    td: ({ node, ...props }) => <td className={`border px-3 py-2 ${message.role === 'user'
                                        ? 'border-purple-300 border-opacity-50'
                                        : 'border-gray-300'
                                        }`} {...props} />,
                                    hr: ({ node, ...props }) => <hr className={`my-4 ${message.role === 'user'
                                        ? 'border-purple-300 border-opacity-50'
                                        : 'border-gray-300'
                                        }`} {...props} />
                                }}
                            >
                                {message.content}
                            </ReactMarkdown>
                        </div>
                    </div>
                    <div className={`text-xs mt-1 ${message.isError
                        ? 'text-red-600'
                        : message.role === 'user'
                            ? 'text-purple-100'
                            : 'text-gray-500'
                        }`}>
                        {showTimestamp && formatDistanceToNow(new Date(message.timestamp), { addSuffix: true })}
                    </div>
                </div>
            </div>
        )
    }


    const ChatContent = () => (
        <div className={`xbg-yellow-500 px-0 pb-0 ${isModal ? 'h-full w-full pt-0' : 'h-[96vh] w-full md:w-full md:max-w-5xl md:mx-20 pt-14'}  relative transform overflow-hidden  text-left shadow-xl transition-all `}>
            {showAgentSelection ? (
                // Agent Selection View - Enhanced UI with loading state
                <div className="xbg-green-500 bg-white h-full mx-0 rounded-none md:rounded-md  flex flex-col overflow-y-auto">

                    <div className="xbg-blue-500  bg-gray-100 px-2 md:px-4 py-2 md:py-3 border-b border-gray-200 flex items-center justify-between">

                        <div className='flex flex-row items-center  gap-x-0 md:gap-x-1'>
                            <NavLogo />
                            <b className="logo-font menu-link-logo-cls md:hidden">we</b>
                            <b className="logo-font logo-light menu-link-logo-cls md:hidden">fayo</b>
                            <b className="logo-font logo-light menu-link-logo-cls">AI</b>
                        </div>
                        <h2 className="text-xs md:text-base font-semibold leading-6 text-gray-900 flex-1 md:flex-none pl-[25px] md:pl-0 ">Select an AI assistant <span className="hidden md:inline">that best fits your needs</span> </h2>
                        {isModal && closeFun && (
                            <button
                                onClick={() => closeFun(false)}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none md:pl-[111.6px]"

                            >
                                <span className="sr-only">Close</span>
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>

                    <div className="xbg-purple-500 flex-1 p-4 md:p-6 md:max-h-[684px] relative flex flex-col justify-start md:justify-start items-start overflow-y-auto ">
                        {isLoadingInitial ? (
                            <div className="flex items-center justify-center h-full w-full">
                                <div className="flex flex-col items-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                                    <div className="mt-2 text-gray-600">Loading your assistants...</div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {isLoadingAgent && (
                                    <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10">
                                        <div className="flex flex-col items-center">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                                            <div className="mt-2 text-gray-600">Loading conversations...</div>
                                        </div>
                                    </div>
                                )}


                                <div className="xbg-pink-500 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 ">
                                    {agents.map((agent) => {
                                        const userSessionsForAgent = userSessions.filter(
                                            session => session.agentType === agent.id && session.isActive
                                        );

                                        // Only count conversations with actual messages
                                        const totalConversations = userSessionsForAgent.reduce((total, session) => {
                                            const conversationsWithMessages = session.conversations ?
                                                session.conversations.filter(conv => conv.messages && conv.messages.length > 0) : [];
                                            return total + conversationsWithMessages.length;
                                        }, 0);

                                        const hasExistingSession = totalConversations > 0;

                                        return (
                                            <button
                                                key={agent.id}
                                                onClick={() => handleAgentSelect(agent)}
                                                className=" h-[160px] md:h-[270px] flex flex-col items-center p-2 md:p-2 md:p-4 rounded-lg border-2 border-gray-200 hover:border-purple-600 hover:shadow-md transition-all duration-200 group text-left relative disabled:opacity-50 disabled:cursor-not-allowed"
                                                disabled={isLoading || isLoadingAgent}
                                            >
                                                {/* {hasExistingSession && (
                                                    <div className="absolute top-2 right-2 min-w-[20px] h-5 bg-purple-600 text-white text-xs rounded-full flex items-center justify-center px-1.5" title={`${totalConversations} conversation${totalConversations !== 1 ? 's' : ''} with messages`}>
                                                        {totalConversations}
                                                    </div>
                                                )} */}

                                                <div className="w-full flex flex-col  items-center gap-x-2 md:gap-x-0">
                                                    <div className=" bg-purple-50 rounded-full p-1 md:p-3 mb-1 md:mb-3 group-hover:bg-purple-600 transition-colors duration-200">
                                                        <agent.icon
                                                            className="h-6 w-6 text-purple-600 group-hover:text-white transition-colors duration-200"
                                                        />
                                                    </div>
                                                    <div className="flex flex-col items-center">
                                                        <h3 className="font-bold text-purple-900 mb-0 md:mb-2 text-lg ">{agent.name}</h3>
                                                        <p className="text-xs md:text-sm text-gray-500 text-left md:text-center font-bold">
                                                            {agent.description}
                                                        </p>
                                                    </div>
                                                </div>

                                                <p className="text-xxs md:text-sm text-gray-500 text-center pt-2">
                                                    {agent.details}
                                                </p>
                                                {/* {hasExistingSession && (
                                                    <p className="text-xs text-purple-600 mt-2 font-medium">
                                                        Continue previous chats
                                                    </p>
                                                )} */}
                                            </button>
                                        );
                                    })}
                                </div>

                                <p className="text-xxs md:text-xs text-gray-500  text-center mt-4 md:mt-4 self-end  ">
                                    <div className='mx-auto max-w-[100%] md:max-w-[80%] px-2 md:px-0'>
                                        The communication offered here is for informational purposes only. Some terms used may be nonstandard or informal. This is not a substitute for professional, individualized advice or support. If you have specific needs or concerns, please consult a qualified professional or trusted support network
                                    </div>
                                </p>
                            </>
                        )}
                    </div>
                </div>
            ) : (
                // Chat Interface with History
                <div className="flex h-full  bg-white rounded-md">
                    {/* Chat History Sidebar - Improved with purple theme */}

                    <ChatHistory
                        conversations={conversations}
                        currentConversationId={currentConversationId}
                        onSelectConversation={handleSelectConversation}
                        selectedAgent={selectedAgent}
                        showChatHistory={showChatHistory}
                        isLoading={isLoading}
                        handleNewConversation={handleNewConversation}
                        onDeleteConversation={handleDeleteConversation}
                        handleBackToAgents={handleBackToAgents}
                    />

                    {/* Main Chat Area */}
                    <div className={`h-full flex-1 flex flex-col space-between ${showChatHistory ? 'hidden md:flex' : ''}`} >
                        <div className="bg-gray-100 px-2 md:px-4 py-2 md:py-3 border-b border-gray-200 flex items-center justify-between">
                            <div className="flex items-center">
                                {/* Remove this button for now, because we want the use to just go back to the landing page */}
                                {/* {selectedAgent && (
                                    <button
                                        onClick={handleBackToAgents}
                                        className="mr-1 md:mr-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                                    >
                                        <ArrowLeft className="h-5 w-5" />
                                    </button>
                                )} */}
                                {selectedAgent && (
                                    <>
                                        <selectedAgent.icon className="w-6 h-6 text-purple-600 mr-0 md:mr-2" />
                                        <h2 className="text-sm md:text-base font-semibold leading-6 text-gray-900">
                                            {selectedAgent.name}
                                        </h2>
                                    </>
                                )}
                            </div>
                            <div className="flex items-center gap-1 md:gap-2">
                                {/* TODO: Add chat history toggle button hidden for now */}
                                <button
                                    onClick={() => setShowChatHistory(true)}
                                    className="text-gray-500 hover:text-gray-700 focus:outline-none  md:hidden"
                                    title="Toggle Chat History"
                                >
                                    {/* {showChatHistory ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />} */}

                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 h-6 w-6">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                                    </svg>

                                </button>
                                {(conversations.length === 0 || conversations.every(conv => conv.messages && conv.messages.length > 0)) && (
                                    <button
                                        onClick={handleNewConversation}
                                        className="text-purple-900 flex items-center gap-1 px-2 md:px-3 py-1 md:py-2 text-sm rounded-md xhover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                                        title="New Conversation"
                                        disabled={isLoading}
                                    >
                                        <Plus className="w-4 h-4" />
                                        New Chat
                                    </button>
                                )}
                                {isModal && closeFun && (
                                    <button
                                        onClick={() => closeFun(false)}
                                        className="text-gray-500 hover:text-gray-700 focus:outline-none"
                                    >
                                        <span className="sr-only">Close</span>
                                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Messages Area */}
                        <div
                            ref={messagesContainerRef}
                            className="flex-1  md:max-h-none overflow-y-auto p-2 md:p-4 space-y-2 md:space-y-4"
                        >
                            {(Array.isArray(messages) && messages.length > 0) && (
                                messages.map((message, index) => <MessageBubble key={message._id || index} message={message} index={index} />
                                ))}

                            {(conversations.length > 0 && messages.length == 0) &&
                                <div className='flex flex-col justify-between items-start h-full gap-y-10'>
                                    {selectedAgent && (
                                        <p>
                                            <MessageBubble message={{
                                                role: 'assistant',
                                                content: selectedAgent.greeting,
                                                timestamp: new Date().toISOString(),
                                            }} index={1} showTimestamp={false} />
                                        </p>
                                    )}
                                    <div className="flex items-center justify-center h-full text-gray-500 w-full">
                                        {/* Enter a topic or question to start a conversation */}
                                    </div>
                                    <div></div>
                                </div>
                            }

                            {(conversations.length == 0 && messages.length == 0) &&
                                <div className='flex flex-col justify-between items-start h-full gap-y-10'>
                                    {selectedAgent && (
                                        <p>
                                            <MessageBubble message={{
                                                role: 'assistant',
                                                content: selectedAgent.greeting,
                                                timestamp: new Date().toISOString(),
                                            }} index={1} showTimestamp={false} />
                                        </p>
                                    )}
                                    <div className="flex items-center justify-center h-full text-gray-500 w-full">
                                        <button
                                            onClick={handleNewConversation}
                                            className="text-white flex items-center gap-1 px-2 md:px-4 py-1 md:py-4 text-sm rounded-md bg-purple-800 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                                            title="New Conversation"
                                            disabled={isLoading}
                                        >
                                            <Plus className="w-4 h-4" />
                                            Start your First Conversation
                                        </button>
                                    </div>
                                    <div></div>
                                </div>
                            }


                            {isLoading && (
                                <div className="flex flex-col justify-start w-full flex gap-y-2">
                                    <div className='flex flex-row justify-end '>
                                        <MessageBubble message={{
                                            role: 'user',
                                            content: messageBeingSent,
                                            timestamp: new Date().toISOString()
                                        }} index={1} />
                                    </div>
                                    <div>
                                        <div className="bg-gray-100 rounded-lg p-3 text-gray-500 max-w-[100px]" >
                                            <div className="flex space-x-2">
                                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Input Area */}
                        {(conversations.length > 0) &&
                            <div className="p-2 md:p-4 border-t border-gray-200">
                                <div className="flex flex-row  gap-2 items-center ">

                                    <IsolatedTextarea
                                        ref={isolatedTextareaRef}
                                        onSend={handleSendMessage}
                                        disabled={isTextareaDisabled}
                                        conversationId={currentConversationId}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleSendButtonClick}
                                        disabled={isSendButtonDisabled}
                                        className="min-w-[50px] md:min-w-[80px] flex items-center justify-center px-1 md:px-3 py-2 h-10 bg-purple-600x text-purple-700 rounded-md hover:bg-purple-700x focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                                    >
                                        {/* <Send className="w-5 h-4" /> */}


                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10 md:w-14 md:h-14">
                                            <path d="M3.478 2.404a.75.75 0 0 0-.926.941l2.432 7.905H13.5a.75.75 0 0 1 0 1.5H4.984l-2.432 7.905a.75.75 0 0 0 .926.94 60.519 60.519 0 0 0 18.445-8.986.75.75 0 0 0 0-1.218A60.517 60.517 0 0 0 3.478 2.404Z" />
                                        </svg>


                                    </button>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            )}
        </div>
    );

    // Return modal version if isModal is true, otherwise return full page version
    if (isModal) {
        return (
            <Transition.Root show={isOpen} as={Fragment}>
                <Dialog as="div" className="relative z-[100]" open={isOpen} onClose={() => closeFun && closeFun(false)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 xbg-red-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-0 md:p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-0 md:rounded-lg bg-white xbg-orange-500 text-left shadow-xl transition-all w-full md:max-w-5xl  h-[100vh] md:h-[calc(100vh-10rem)] lg:max-h-[800px]  ">
                                    <ChatContent />
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        );
    }

    return <ChatContent />;
};

export default WefayoAIChatInterface;
